import { Box, Button, Menu, MenuButton, MenuItem, MenuList, Flex } from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import upperFirst from 'lodash-es/upperFirst.js';

import { SortOrder } from '@duke-hq/lib/pagination';
import {
  SupportTaskProcessor,
  SupportTaskStatus,
  SupportTaskKind,
} from '@duke-hq/svc-api-db/client';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useAuthAdmin } from '../AuthProviderAdmin';
import { formatTaskType } from './utils/formatters';
import { getStatesOptions } from '../constants/student';
import { iconSize, menuMaxHeight } from '@duke-hq/ui/constants/scalable';

interface Filters {
  orderBy?: string;
  sort?: SortOrder;
  processor?: SupportTaskProcessor;
  status?: SupportTaskStatus;
  kind?: SupportTaskKind;
  assignedTo?: string;
  providerIds?: string;
  state?: string;
}

function formatFilterTitle(label: string) {
  return upperFirst(label.replace(/_/g, ' ').toLowerCase());
}

export function SupportTaskFilters({
  filters,
  onChange,
  availableStates,
  availableProviderIds,
  availableAssigneeDrns,
  hasUnassignedTasks,
}: {
  filters: Filters;
  onChange: (newFilters: Filters) => void;
  availableStates?: string[];
  availableProviderIds?: number[];
  availableAssigneeDrns?: string[];
  hasUnassignedTasks?: boolean;
}) {
  const session = useAuthAdmin();
  const { processor, status, kind } = filters;

  const handleFilterChange = useCallback(
    (key: keyof Filters, value: string | undefined) => {
      onChange({ ...filters, [key]: value });
    },
    [filters, onChange],
  );

  const handleMultiFilterChange = useCallback(
    (update: MultipleChoiceFilterOption) => {
      const [key, values] = Object.entries(update)[0] as [string, string[]];
      onChange({
        ...filters,
        [key]: values.length > 0 ? values.join(',') : undefined,
      });
    },
    [filters, onChange],
  );

  const statusOptions = useMemo(() => {
    return Object.values(SupportTaskStatus).map((s) => ({
      id: s,
      value: s,
      label: formatFilterTitle(s),
    }));
  }, []);

  const kindOptions = useMemo(() => {
    return Object.values(SupportTaskKind).map((k) => ({
      id: k,
      value: k,
      label: formatTaskType(k, null),
    }));
  }, []);

  const { data: users } = trpcAdmin.users.all.useQuery({}, { initialData: [] });

  const assigneeOptions = useMemo(() => {
    const currentUserDrn = session.user?.drn;
    const drnsSet = availableAssigneeDrns != null ? new Set(availableAssigneeDrns) : null;

    const options: { id: string; value: string; label: string }[] = [];

    if (currentUserDrn != null && (drnsSet == null || drnsSet.has(currentUserDrn))) {
      options.push({ id: currentUserDrn, value: currentUserDrn, label: t`Assigned to me` });
    }

    if (hasUnassignedTasks !== false) {
      options.push({ id: 'unassigned', value: 'unassigned', label: t`Unassigned` });
    }

    const otherUserOptions = users
      .filter(
        (user) =>
          user.drn !== currentUserDrn &&
          user.drn != null &&
          (drnsSet == null || drnsSet.has(user.drn)),
      )
      .map((user) => ({
        id: user.drn ?? '',
        value: user.drn ?? '',
        label: `${user.first_name} ${user.last_name}`,
      }));

    return [...options, ...otherUserOptions];
  }, [session.user?.drn, users, availableAssigneeDrns, hasUnassignedTasks]);

  const { data: providers } = trpcAdmin.providers.all.useQuery({}, { initialData: [] });

  const providerOptions = useMemo(() => {
    return providers
      .filter((p) => availableProviderIds == null || availableProviderIds.includes(p.id))
      .map((p) => ({
        id: String(p.id),
        value: String(p.id),
        label: p.name,
      }));
  }, [providers, availableProviderIds]);

  const locationOptions = useMemo(
    () =>
      getStatesOptions()
        .filter((s) => availableStates == null || availableStates.includes(s.value))
        .map((s) => ({
          id: s.value,
          value: s.value,
          label: s.label,
        })),
    [availableStates],
  );

  const processorLabel =
    processor === SupportTaskProcessor.HUMAN
      ? t`Manual Tasks`
      : processor === SupportTaskProcessor.AUTOMATED
        ? t`Automated Tasks`
        : t`Task Mode`;

  const statusLabel = status != null ? formatFilterTitle(status) : t`Status`;
  const kindLabel = kind != null ? formatTaskType(kind, null) : t`Type`;

  return (
    <Flex gap={2}>
      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          px={2}
          colorScheme="gray"
          rightIcon={
            status != null ? (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange('status', undefined);
                }}
              >
                <X size={iconSize.md} />
              </Box>
            ) : (
              <ChevronDown size={iconSize.md} />
            )
          }
        >
          {statusLabel}
        </MenuButton>
        <MenuList>
          {statusOptions.map((option) => (
            <MenuItem key={option.id} onClick={() => handleFilterChange('status', option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          px={2}
          colorScheme="gray"
          rightIcon={
            kind != null ? (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange('kind', undefined);
                }}
              >
                <X size={iconSize.md} />
              </Box>
            ) : (
              <ChevronDown size={iconSize.md} />
            )
          }
        >
          {kindLabel}
        </MenuButton>
        <MenuList maxH={menuMaxHeight} overflowY="auto">
          {kindOptions.map((option) => (
            <MenuItem key={option.id} onClick={() => handleFilterChange('kind', option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <MultipleChoiceFilter
        items={providerOptions}
        label={t`Provider`}
        name="providerIds"
        value={filters.providerIds}
        onFilterChange={handleMultiFilterChange}
        onlyUpdateOnChange={true}
      />

      <MultipleChoiceFilter
        items={locationOptions}
        label={t`Location`}
        name="state"
        value={filters.state}
        showSearch={false}
        onFilterChange={handleMultiFilterChange}
        onlyUpdateOnChange={true}
      />

      <MultipleChoiceFilter
        items={assigneeOptions}
        label={t`Assignee`}
        name="assignedTo"
        value={filters.assignedTo}
        onFilterChange={handleMultiFilterChange}
        onlyUpdateOnChange={true}
      />

      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          px={2}
          colorScheme="gray"
          rightIcon={
            processor != null ? (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange('processor', undefined);
                }}
              >
                <X size={iconSize.md} />
              </Box>
            ) : (
              <ChevronDown size={iconSize.md} />
            )
          }
        >
          {processorLabel}
        </MenuButton>
        <MenuList>
          <MenuItem onClick={() => handleFilterChange('processor', undefined)}>
            {t`All Tasks`}
          </MenuItem>
          <MenuItem onClick={() => handleFilterChange('processor', SupportTaskProcessor.HUMAN)}>
            {t`Manual Tasks`}
          </MenuItem>
          <MenuItem onClick={() => handleFilterChange('processor', SupportTaskProcessor.AUTOMATED)}>
            {t`Automated Tasks`}
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}
